import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { cashReceipts, type CashReceiptListItem } from '@/api/cashDocs';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nowrap: React.CSSProperties = { whiteSpace: 'nowrap' };

export default function CashReceiptsPage() {
  // 2026-08-23 (Норма #29): серверна пагінація/сортування замість «голого»
  // AgGridReact; колонка «Підпис» (sign-control).
  // ч.949 (рішення власника): рядок фільтрів — організація, період, платник,
  // ПIДСТАВА; орг і період слідують за шапкою, доки не змінені тут вручну.
  const dp = useDefaultPeriod();
  const [orgId, setOrgId] = useOrgFilter();
  const [ovrFrom, setOvrFrom] = useState<string | null>(null);
  const [ovrTo, setOvrTo] = useState<string | null>(null);
  useEffect(() => { setOvrFrom(null); setOvrTo(null); }, [dp.dateFrom, dp.dateTo]);
  const dateFromDay = ovrFrom ?? (dp.dateFrom ? dp.dateFrom.slice(0, 10) : '');
  const dateToDay = ovrTo ?? (dp.dateTo ? dp.dateTo.slice(0, 10) : '');
  const dateFrom = dateFromDay ? `${dateFromDay}T00:00:00` : undefined;
  const dateTo = dateToDay ? `${dateToDay}T23:59:59` : undefined;
  const [contractorId, setContractorId] = useState('');
  const [basis, setBasis] = useState('');
  const [basisQ, setBasisQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setBasisQ(basis.trim()), 400);
    return () => clearTimeout(id);
  }, [basis]);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { datasource, total } = useInfiniteGridDatasource<CashReceiptListItem>(
    (limit, offset, sort) => cashReceipts.list({
      organization_id: orgId || undefined,
      contractor_id: contractorId || undefined,
      basis: basisQ || undefined,
      date_from: dateFrom, date_to: dateTo,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId, contractorId, basisQ, dateFrom, dateTo],
    { docType: 'doc.cash_receipts' },
  );
  const sc = useSignControlInfinite<CashReceiptListItem>('doc.cash_receipts', orgId);
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const cols = useMemo<ColDef<CashReceiptListItem>[]>(() => [
    { field: 'document_number', headerName: t('cd.number'), width: 150 },
    {
      field: 'document_date', headerName: t('cd.date'), width: 170,
      valueFormatter: (p) => p.value ? new Date(p.value as string).toLocaleString(i18n.language) : '',
    },
    {
      field: 'received_from', headerName: t('cr.received_from'), flex: 1, minWidth: 200,
      valueFormatter: (p) => {
        if (p.value) return p.value as string;
        const cId = p.data?.contractor_id;
        return cId ? (cName.get(cId) ?? '') : '';
      },
    },
    { field: 'basis', headerName: t('cd.basis'), flex: 1, minWidth: 200 },
    {
      field: 'amount', headerName: t('cd.amount'), width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => fmt(p.value as string),
    },
    {
      field: 'posted', headerName: t('cd.posted'), width: 130,
      valueFormatter: (p) => (p.value ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no')),
    },
  ], [t, i18n.language, cName]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('cr.title')}<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/cash-receipts/new')}>
          + {t('cr.new')}
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap',
        marginBottom: '0.5rem', minWidth: 0 }}>
        <select value={orgId} style={{ width: 170, ...nowrap }}
          title="Організація: слідує за верхнім фільтром шапки, доки не обрано тут вручну"
          onChange={(e) => setOrgId(e.target.value)}>
          <option value="">Усі організації</option>
          {(orgListQ.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <div style={{ width: 108, ...nowrap }}>
          <DatePopover value={dateFromDay} onChange={(v) => setOvrFrom(v)} />
        </div>
        <span style={{ color: 'var(--muted)' }}>—</span>
        <div style={{ width: 108, ...nowrap }}>
          <DatePopover value={dateToDay} onChange={(v) => setOvrTo(v)} />
        </div>
        <div style={{ width: 190, ...nowrap }}>
          <SearchSelect
            items={(cQ.data?.items ?? []).map((c) => ({ id: c.id, label: c.name, hint: c.edrpou ?? null }))}
            value={contractorId}
            onChange={(id) => setContractorId(id)}
            placeholder="Платник: усі"
          />
        </div>
        <input value={basis} style={{ width: 200, ...nowrap }}
          placeholder="Підстава: пошук"
          title="Фільтр за підставою документа (частина тексту)"
          onChange={(e) => setBasis(e.target.value)} />
      </div>
      <InfiniteDocGrid<CashReceiptListItem>
        copyRow={(r) => navigate(`/cash-receipts/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => cashReceipts.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="cash-receipts"
        onRowDoubleClicked={(r) => navigate(`/cash-receipts/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
